import { Row } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import { DeleteClient } from "./delete-client";
import { useAuth } from "../../lib/AuthContext";
import { useClientLockForClient } from "../../hooks/useClientLock";
import ClientLockIndicator from "../clients/ClientLockIndicator";
import { CopyClientButton } from "../clients/copy-client-button";
import React from "react";
import { useNavigate } from "react-router-dom";

interface Client {
  id: string;
}

interface DataTableRowActionsProps {
  row: Row<Client>;
}

export function DataTableRowActions({ row }: DataTableRowActionsProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const permissions = auth.currentUser?.user_permissions[0];
  const client = row.original;

  // Check if client locking feature is enabled via database feature flag
  const isClientLockingEnabled =
    auth?.featureFlags?.includes("client_lock") || false;

  // Client lock functionality (only if feature is enabled)
  const { isLocked, isLockedByMe, lockInfo } = useClientLockForClient(
    client.id
  );

  const handleEdit = () => {
    // If client locking is enabled, check if locked by someone else
    if (isClientLockingEnabled) {
      // If client is locked by someone else, don't allow editing
      if (isLocked && !isLockedByMe) {
        const message = `This client is currently being edited by ${lockInfo?.userName || "another user"}. Please try again later.`;
        console.warn(message);
        return;
      }
    }

    // Use React Router navigate instead of window.location to ensure cleanup runs
    navigate(`/edit-client/${client.id}`);
  };

  const shouldShowIndicator = isClientLockingEnabled && isLocked;
  const shouldShowEditButton =
    permissions?.can_manage_clients &&
    !(isClientLockingEnabled && isLocked && !isLockedByMe);

  return (
    <div className="flex items-center gap-2">
      {/* Lock indicator - only show if feature is enabled */}
      {shouldShowIndicator && (
        <ClientLockIndicator
          clientId={client.id}
          isLocked={isLocked}
          isLockedByMe={isLockedByMe}
          lockInfo={lockInfo}
        />
      )}

      {/* Edit button - only show if NOT locked by another user */}
      {shouldShowEditButton && (
        <Button
          variant="outline"
          onClick={handleEdit}
          className="hover:bg-accent"
        >
          Edit
        </Button>
      )}

      {/* Copy button - only show if NOT locked by another user */}
      {shouldShowEditButton && (
        <CopyClientButton 
          client={client}
          variant="outline"
          size="icon"
          showText={false}
          onSuccess={(newClientId) => {
            navigate(`/edit-client/${newClientId}`);
          }}
        />
      )}

      {/* Delete button - only show if NOT locked by another user */}
      {shouldShowEditButton && <DeleteClient id={client.id} />}
    </div>
  );
}
